import { takeLatest, put, select } from 'redux-saga/effects';
import { get, isEqual } from 'lodash';
import update from 'immutability-helper';
import {
  nocTechErrorMetricsFeatureSelector,
  supportedMetricsSelector,
} from '@store/common/selectors';
import { getRulesFromFilter, getFilterFromRules } from '@utils';
import {
  ERROR_METRICS,
  TECH_ERROR_METRICS,
  METRIC_SWITCH_MAP,
  NOC_EXCLUDE_METRICS,
  getMetricShortName,
} from '@utils/metrics';
import { DASHBOARD_TYPE, isAppModule, NOC_MODE_TRENDS } from '@utils/constants';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { excludeWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { parseMetricsFn } from '@store/common/persistence';
import * as actions from './actions';
import * as types from './types';
import baseSelector from './selectors';
import { setTempIdToFilters } from './reducer';
import { reset as resetNocState } from '../actions';

// filter out persisted/saved merics based on new feature flags
function* filterMetrics(metrics = []) {
  // valid metrics
  const supportedMetrics = yield select(supportedMetricsSelector);
  const parseMetrics = parseMetricsFn(supportedMetrics);

  const enableErrorMetrics = yield select(nocTechErrorMetricsFeatureSelector);
  let filteredMetrics: any = [];
  if (enableErrorMetrics) {
    filteredMetrics = excludeWithMetricDeDuplication(metrics, ERROR_METRICS);
  } else {
    filteredMetrics = excludeWithMetricDeDuplication(metrics, TECH_ERROR_METRICS);
  }

  // filter out excluded noc metrics
  filteredMetrics = excludeWithMetricDeDuplication(filteredMetrics, NOC_EXCLUDE_METRICS);

  return parseMetrics(filteredMetrics);
}

// filter and apply persisted state
function* filterAndapply({ base }) {
  const baseState = yield select(baseSelector);

  const filteredNocState = update(base, {
    filters: {
      metrics: {
        $set: yield filterMetrics(base.filters.metrics),
      },
      appliedFilter: {
        $set: setTempIdToFilters(base.filters.appliedFilter),
      },
    },
    trends: {
      metrics: {
        $set: yield filterMetrics(base.trends.metrics),
      },
      appliedFilter: {
        $set: setTempIdToFilters(base.trends.appliedFilter),
      },
    },
  });

  if (!isEqual(baseState, filteredNocState)) {
    // clear previous state and data
    yield put(resetNocState());
    yield put(actions.setPersistedState(filteredNocState));
  }
}

// trigger action for persistence saga
// for all the relevant events in NOC
function* triggerPersistence() {
  const baseState = yield select(baseSelector);
  yield put(actions.triggerPersistence({ noc: baseState }));
}

function* handlePersistedState(action) {
  // persistence action is common
  // ignore if triggered from another tab
  const key = get(action, ['response', 'key'], null);
  const { meta = {} } = action;

  if (key !== '/noc') {
    return;
  }

  // check for noc persistence
  const persistentNoc = get(action, ['response', 'configuration', 'noc'], null);

  if (persistentNoc) {
    yield filterAndapply({ base: persistentNoc });
  } else {
    // clear previous state and data
    yield put(resetNocState());

    // default to trends view
    yield put(actions.setMode(NOC_MODE_TRENDS));
  }

  if ('doneCallback' in meta) {
    meta.doneCallback();
  }
}

function* applyDrilldown(action) {
  const { metric, rules } = action.params;

  const newRules = getRulesFromFilter(rules, true);
  const filter = getFilterFromRules(newRules, true);

  // drill down to temp dashboard
  const dashboard = {
    filter,
    metric: METRIC_SWITCH_MAP[metric] || metric,
  };

  yield put(
    createTempDashboard(
      isAppModule ? DASHBOARD_TYPE.APP_NOC_DRILLDOWN : DASHBOARD_TYPE.NOC_DRILLDOWN,
      dashboard,
      `${getMetricShortName({ metricKey: metric, hasSubscript: false })} - Real Time`,
    ),
  );
}

function* watchPersistedState() {
  yield takeLatest([types.UPDATE_PERSISTED_NOC_STATE], handlePersistedState);
}

// filter, metric, dimension or mode change will trigger persistence
function* watchActions() {
  yield takeLatest(
    [
      types.SET_DIMENSION,
      types.SET_TRENDS_METRICS,
      types.SET_FILTERS_METRICS,
      types.UPDATE_SAVED_FILTERS_SELECTED_NOC_FILTERS,
      types.APPLY_FILTERS_SELECTED_NOC_FILTERS,
      types.APPLY_TRENDS_SELECTED_NOC_FILTERS,
      types.SET_MODE,
      types.SET_TRENDS_SORTING,
      types.SET_FILTERS_SORTING,
      types.SET_AUTO_SORT,
    ],
    triggerPersistence,
  );
}

function* watchFilterAndApply() {
  yield takeLatest(types.FILTER_AND_APPLY_STATE, filterAndapply);
}

function* watchDrilldown() {
  yield takeLatest([types.APPLY_DRILLDOWN], applyDrilldown);
}

export default [watchActions, watchPersistedState, watchDrilldown, watchFilterAndApply];
